import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  LinearProgress,
  Divider,
  Stack,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  MdAnalytics,
  MdUploadFile,
  MdDashboard,
  MdFilterList,
  MdClear,
  MdRefresh,
  MdFullscreen,
  MdSettings
} from 'react-icons/md';
import { FaChartBar, FaTable, FaFilter } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { useTheme } from '../../context/ThemeContext';
import { API_ENDPOINTS, apiCall } from '../../config/api';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 8px 25px rgba(0,0,0,0.4)' 
      : '0 8px 25px rgba(0,0,0,0.15)',
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 24px',
  '&:hover': {
    transform: 'translateY(-1px)',
  }
}));

const PowerBIContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '600px',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  overflow: 'hidden',
  position: 'relative',
  '& iframe': {
    width: '100%',
    height: '100%',
    border: 'none'
  }
}));

const FilterChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  borderRadius: 16,
}));

const SalesPerformanceDashboard = () => {
  const { isDarkMode } = useTheme();
  const powerBIRef = useRef(null);
  
  // State management
  const [selectedReport, setSelectedReport] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [powerBIReport, setPowerBIReport] = useState(null);
  const [embedToken, setEmbedToken] = useState(null);
  const [reportData, setReportData] = useState([]);
  
  // Power BI filter state
  const [activeFilters, setActiveFilters] = useState([]);
  const [availableFilters, setAvailableFilters] = useState([]);
  const [filterValues, setFilterValues] = useState({});

  // Sample reports list
  const reports = [
    {
      id: 'shipped-orders-summary',
      name: 'Shipped Orders Summary',
      description: 'Interactive Power BI dashboard with shipped orders analysis',
      icon: FaChartBar,
      active: true,
      workspaceId: 'your-workspace-id', // Replace with actual workspace ID
      reportId: 'your-report-id' // Replace with actual report ID
    },
    {
      id: 'pending-orders',
      name: 'Pending Orders Analysis',
      description: 'Track orders awaiting shipment with Power BI visuals',
      icon: FaTable,
      active: false
    },
    {
      id: 'customer-insights',
      name: 'Customer Insights',
      description: 'Customer analytics with Power BI interactive charts',
      icon: MdAnalytics,
      active: false
    }
  ];

  // Load Power BI JavaScript SDK
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/powerbi-client@2.22.0/dist/powerbi.min.js';
    script.async = true;
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  // File upload and data processing
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    setError('');

    try {
      const data = await parseFile(file);
      setReportData(data);
      setUploadedFile(file);
      
      // Extract potential filter columns from the data
      if (data.length > 0) {
        const columns = Object.keys(data[0]);
        const filterableColumns = columns.filter(col => 
          col.toLowerCase().includes('date') ||
          col.toLowerCase().includes('status') ||
          col.toLowerCase().includes('category') ||
          col.toLowerCase().includes('region') ||
          col.toLowerCase().includes('customer') ||
          col.toLowerCase().includes('product')
        );
        setAvailableFilters(filterableColumns);
      }
      
    } catch (error) {
      setError('Error processing file: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Parse file helper function
  const parseFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          let workbook;
          
          if (file.name.toLowerCase().endsWith('.csv')) {
            const csvText = new TextDecoder('utf-8').decode(data);
            const lines = csvText.split('\n').filter(line => line.trim());
            const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
            const jsonData = lines.slice(1).map((line, index) => {
              const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
              const row = {};
              headers.forEach((header, i) => {
                row[header] = values[i] || '';
              });
              return row;
            });
            resolve(jsonData);
          } else {
            workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);
            resolve(jsonData);
          }
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error('File reading failed'));
      reader.readAsArrayBuffer(file);
    });
  };

  // Create Power BI dashboard
  const handleCreateDashboard = async () => {
    if (!reportData.length || !selectedReport) return;

    setLoading(true);
    setError('');

    try {
      // Step 1: Upload data to Power BI dataset
      const datasetResponse = await uploadDataToPowerBI(reportData, selectedReport.workspaceId);
      
      if (!datasetResponse.success) {
        throw new Error(datasetResponse.message);
      }

      // Step 2: Get embed token for the report
      const embedResponse = await getEmbedToken(selectedReport.reportId, selectedReport.workspaceId);
      
      if (!embedResponse.success) {
        throw new Error(embedResponse.message);
      }

      setEmbedToken(embedResponse.embedToken);
      
      // Step 3: Embed the Power BI report
      await embedPowerBIReport(embedResponse);
      
    } catch (error) {
      setError('Error creating Power BI dashboard: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Upload data to Power BI dataset
  const uploadDataToPowerBI = async (data, workspaceId) => {
    try {
      return await apiCall(API_ENDPOINTS.POWERBI_UPLOAD_DATA, {
        method: 'POST',
        body: JSON.stringify({
          data: data,
          workspaceId: workspaceId,
          datasetName: 'Shipped Orders Dataset'
        })
      });
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  // Get Power BI embed token
  const getEmbedToken = async (reportId, workspaceId) => {
    try {
      return await apiCall(API_ENDPOINTS.POWERBI_EMBED_TOKEN(reportId), {
        method: 'POST',
        body: JSON.stringify({
          workspaceId: workspaceId
        })
      });
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  // Embed Power BI report using JavaScript SDK
  const embedPowerBIReport = async (embedData) => {
    if (!window.powerbi || !powerBIRef.current) return;

    const config = {
      type: 'report',
      id: selectedReport.reportId,
      embedUrl: embedData.embedUrl,
      accessToken: embedData.embedToken,
      tokenType: window.models.TokenType.Embed,
      settings: {
        panes: {
          filters: {
            expanded: false,
            visible: true
          },
          pageNavigation: {
            visible: true
          }
        },
        background: isDarkMode ? window.models.BackgroundType.Transparent : window.models.BackgroundType.Default
      }
    };

    const report = window.powerbi.embed(powerBIRef.current, config);
    setPowerBIReport(report);

    // Handle report events
    report.on('loaded', () => {
      console.log('Power BI report loaded successfully');
    });

    report.on('rendered', () => {
      console.log('Power BI report rendered successfully');
    });

    report.on('error', (event) => {
      console.error('Power BI report error:', event.detail);
      setError('Power BI report error: ' + event.detail);
    });
  };

  // Apply Power BI filter
  const applyPowerBIFilter = async (column, values) => {
    if (!powerBIReport) return;

    try {
      const filter = {
        $schema: "http://powerbi.com/product/schema#basic",
        target: {
          table: "ShippedOrders", // Replace with your table name
          column: column
        },
        operator: "In",
        values: Array.isArray(values) ? values : [values]
      };

      await powerBIReport.updateFilters(window.models.FiltersOperations.Replace, [filter]);
      
      // Update active filters state
      setActiveFilters(prev => {
        const updated = prev.filter(f => f.column !== column);
        if (values && values.length > 0) {
          updated.push({ column, values });
        }
        return updated;
      });
    } catch (error) {
      console.error('Error applying filter:', error);
      setError('Error applying filter: ' + error.message);
    }
  };

  // Remove Power BI filter
  const removePowerBIFilter = async (column) => {
    if (!powerBIReport) return;

    try {
      const filters = await powerBIReport.getFilters();
      const updatedFilters = filters.filter(f => 
        !(f.target && f.target.column === column)
      );
      
      await powerBIReport.updateFilters(window.models.FiltersOperations.Replace, updatedFilters);
      
      setActiveFilters(prev => prev.filter(f => f.column !== column));
      setFilterValues(prev => {
        const updated = { ...prev };
        delete updated[column];
        return updated;
      });
    } catch (error) {
      console.error('Error removing filter:', error);
    }
  };

  // Get unique values for filter dropdown
  const getUniqueValues = (column) => {
    if (!reportData.length) return [];
    const values = [...new Set(reportData.map(row => row[column]))];
    return values.filter(value => value !== null && value !== undefined && value !== '').slice(0, 50);
  };

  return (
    <Box sx={{ p: 3, minHeight: '100vh' }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Sales Performance Dashboard
      </Typography>

      <Grid container spacing={3}>
        {/* Left Sidebar - Reports List */}
        <Grid item xs={12} md={3}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Power BI Reports
              </Typography>
              <List>
                {reports.map((report) => {
                  const IconComponent = report.icon;
                  return (
                    <ListItem
                      key={report.id}
                      button
                      selected={selectedReport?.id === report.id}
                      onClick={() => setSelectedReport(report)}
                      disabled={!report.active}
                      sx={{
                        borderRadius: 2,
                        mb: 1,
                        '&.Mui-selected': {
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          }
                        }
                      }}
                    >
                      <ListItemIcon>
                        <IconComponent 
                          size={20} 
                          color={selectedReport?.id === report.id ? 'white' : undefined}
                        />
                      </ListItemIcon>
                      <ListItemText 
                        primary={report.name}
                        secondary={report.active ? report.description : 'Coming soon...'}
                        secondaryTypographyProps={{
                          color: selectedReport?.id === report.id ? 'inherit' : 'text.secondary'
                        }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </StyledCard>
        </Grid>

        {/* Main Content */}
        <Grid item xs={12} md={9}>
          {!selectedReport ? (
            <StyledCard sx={{ height: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box textAlign="center">
                <MdAnalytics size={64} color="#ccc" />
                <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
                  Select a Power BI report from the left to get started
                </Typography>
              </Box>
            </StyledCard>
          ) : (
            <Box>
              {/* Report Header */}
              <StyledCard sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {selectedReport.name}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {selectedReport.description}
                  </Typography>
                </CardContent>
              </StyledCard>

              {/* File Upload Section */}
              {!powerBIReport && (
                <StyledCard sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Upload Data for Power BI Dashboard
                    </Typography>
                    
                    {error && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                      </Alert>
                    )}
                    
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                      id="file-upload"
                    />
                    
                    <Stack direction="row" spacing={2} alignItems="center">
                      <label htmlFor="file-upload">
                        <StyledButton
                          variant="outlined"
                          component="span"
                          startIcon={<MdUploadFile />}
                          disabled={loading}
                        >
                          Choose File
                        </StyledButton>
                      </label>
                      
                      {uploadedFile && (
                        <Typography variant="body2" color="text.secondary">
                          {uploadedFile.name} ({reportData.length} records)
                        </Typography>
                      )}
                      
                      {uploadedFile && !powerBIReport && (
                        <StyledButton
                          variant="contained"
                          startIcon={<MdDashboard />}
                          onClick={handleCreateDashboard}
                          disabled={loading || reportData.length === 0}
                        >
                          Create Power BI Dashboard
                        </StyledButton>
                      )}
                    </Stack>
                    
                    {loading && <LinearProgress sx={{ mt: 2 }} />}
                  </CardContent>
                </StyledCard>
              )}

              {/* Power BI Filters Section */}
              {powerBIReport && availableFilters.length > 0 && (
                <StyledCard sx={{ mb: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <MdFilterList size={24} />
                      <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
                        Power BI Filters
                      </Typography>
                      <Tooltip title="Refresh Report">
                        <IconButton onClick={() => powerBIReport.reload()}>
                          <MdRefresh />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* Filter Controls */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {availableFilters.slice(0, 4).map((column) => (
                        <Grid item xs={12} sm={6} md={3} key={column}>
                          <FormControl fullWidth size="small">
                            <InputLabel>{column}</InputLabel>
                            <Select
                              value={filterValues[column] || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                setFilterValues(prev => ({ ...prev, [column]: value }));
                                applyPowerBIFilter(column, value ? [value] : []);
                              }}
                              label={column}
                            >
                              <MenuItem value="">
                                <em>All</em>
                              </MenuItem>
                              {getUniqueValues(column).map((value) => (
                                <MenuItem key={value} value={value}>
                                  {String(value)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      ))}
                    </Grid>

                    {/* Active Filter Chips */}
                    {activeFilters.length > 0 && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Active Filters:
                        </Typography>
                        {activeFilters.map((filter) => (
                          <FilterChip
                            key={filter.column}
                            label={`${filter.column}: ${filter.values.join(', ')}`}
                            onDelete={() => removePowerBIFilter(filter.column)}
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </StyledCard>
              )}

              {/* Power BI Report Container */}
              {powerBIReport ? (
                <StyledCard>
                  <CardContent sx={{ p: 0 }}>
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ flexGrow: 1 }}>
                        Power BI Interactive Dashboard
                      </Typography>
                      <Tooltip title="Fullscreen">
                        <IconButton onClick={() => powerBIReport.fullscreen()}>
                          <MdFullscreen />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    
                    <PowerBIContainer ref={powerBIRef} />
                  </CardContent>
                </StyledCard>
              ) : embedToken && (
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" textAlign="center" color="text.secondary">
                      Loading Power BI Dashboard...
                    </Typography>
                    <LinearProgress sx={{ mt: 2 }} />
                  </CardContent>
                </StyledCard>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default SalesPerformanceDashboard;